import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { 
  Mail, 
  Settings, 
  Send, 
  CheckCircle, 
  XCircle, 
  Loader2,
  Eye,
  EyeOff,
  Server,
  Shield,
  TestTube,
  ChevronDown,
  Save,
  Info
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const EmailSettings = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [presets, setPresets] = useState([]);
  const [selectedPreset, setSelectedPreset] = useState('');
  const [hasExistingSettings, setHasExistingSettings] = useState(false);
  
  const [settings, setSettings] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_username: '',
    smtp_password: '',
    from_email: '',
    from_name: '',
    use_tls: true,
    use_ssl: false
  });
  
  const [testEmail, setTestEmail] = useState('');
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    fetchSettings();
    fetchPresets();
  }, []);

  const fetchSettings = async () => {
    try {
      const res = await fetch(
        `${API_URL}/api/email/settings/${user?.organization_id}?user_id=${user?.id}`
      );
      const data = await res.json();
      
      if (data.settings) {
        setSettings(prev => ({
          ...prev,
          ...data.settings,
          smtp_password: '' // Don't show existing password
        }));
        setHasExistingSettings(true);
      }
    } catch (error) {
      console.error('Failed to fetch settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPresets = async () => {
    try {
      const res = await fetch(`${API_URL}/api/email/presets`);
      const data = await res.json();
      setPresets(data.presets || []);
    } catch (error) {
      console.error('Failed to fetch presets:', error);
    }
  };

  const handlePresetChange = (presetName) => {
    setSelectedPreset(presetName);
    const preset = presets.find(p => p.name === presetName);
    if (preset) {
      setSettings(prev => ({
        ...prev,
        smtp_host: preset.smtp_host,
        smtp_port: preset.smtp_port,
        use_tls: preset.use_tls,
        use_ssl: preset.use_ssl
      }));
    }
  };

  const handleSave = async () => {
    if (!settings.smtp_host || !settings.smtp_username || !settings.from_email) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    if (!hasExistingSettings && !settings.smtp_password) {
      toast.error('Password is required');
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/email/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          organization_id: user?.organization_id,
          user_id: user?.id,
          settings: settings,
          is_default: false
        })
      });
      
      const data = await res.json();
      
      if (res.ok) {
        toast.success('Email settings saved successfully');
        setHasExistingSettings(true);
      } else {
        toast.error(data.detail || 'Failed to save settings');
      }
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleTest = async () => {
    if (!testEmail) {
      toast.error('Please enter a test email address');
      return;
    }

    if (!settings.smtp_host || !settings.smtp_username || !settings.smtp_password) {
      toast.error('Please fill in all SMTP settings including password');
      return;
    }

    setTesting(true);
    setTestResult(null);
    
    try {
      const res = await fetch(`${API_URL}/api/email/test-connection`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          settings: settings,
          test_email: testEmail
        })
      });
      
      const data = await res.json();
      setTestResult(data);
      
      if (data.success) {
        toast.success('Test email sent! Check your inbox.');
      } else {
        toast.error(data.message || 'Connection test failed');
      }
    } catch (error) {
      setTestResult({ success: false, message: 'Connection failed' });
      toast.error('Connection test failed');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Mail className="h-6 w-6 text-primary" />
              Email Settings
            </h1>
            <p className="text-muted-foreground mt-1">
              Configure your email to send invoices, quotations, and documents
            </p>
          </div>
        </div>

        {/* Info Banner */}
        <div className="p-4 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg flex items-start gap-3">
          <Info className="h-5 w-5 text-blue-600 mt-0.5" />
          <div className="text-sm">
            <p className="font-medium text-blue-900 dark:text-blue-100">Configure your email provider</p>
            <p className="text-blue-700 dark:text-blue-300">
              Set up SMTP to send invoices, quotations, purchase orders, and expense reports directly from the app.
              Works with Outlook, Gmail, Yahoo, and custom SMTP servers.
            </p>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-2">
          {/* SMTP Configuration Card */}
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Server className="h-5 w-5" />
                SMTP Configuration
              </CardTitle>
              <CardDescription>
                Enter your email server settings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Preset Selector */}
              <div className="space-y-2">
                <Label>Email Provider Preset</Label>
                <select
                  className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                  value={selectedPreset}
                  onChange={(e) => handlePresetChange(e.target.value)}
                >
                  <option value="">Select a preset...</option>
                  {presets.map(preset => (
                    <option key={preset.name} value={preset.name}>
                      {preset.name}
                    </option>
                  ))}
                </select>
                {selectedPreset && presets.find(p => p.name === selectedPreset)?.notes && (
                  <p className="text-xs text-muted-foreground">
                    💡 {presets.find(p => p.name === selectedPreset)?.notes}
                  </p>
                )}
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                {/* SMTP Host */}
                <div className="space-y-2">
                  <Label htmlFor="smtp_host">SMTP Host *</Label>
                  <Input
                    id="smtp_host"
                    placeholder="smtp.office365.com"
                    value={settings.smtp_host}
                    onChange={(e) => setSettings({...settings, smtp_host: e.target.value})}
                  />
                </div>

                {/* SMTP Port */}
                <div className="space-y-2">
                  <Label htmlFor="smtp_port">SMTP Port *</Label>
                  <Input
                    id="smtp_port"
                    type="number"
                    placeholder="587"
                    value={settings.smtp_port}
                    onChange={(e) => setSettings({...settings, smtp_port: parseInt(e.target.value)})}
                  />
                </div>

                {/* Username */}
                <div className="space-y-2">
                  <Label htmlFor="smtp_username">Username / Email *</Label>
                  <Input
                    id="smtp_username"
                    placeholder="your-email@company.com"
                    value={settings.smtp_username}
                    onChange={(e) => setSettings({...settings, smtp_username: e.target.value})}
                  />
                </div>

                {/* Password */}
                <div className="space-y-2">
                  <Label htmlFor="smtp_password">
                    Password * {hasExistingSettings && <span className="text-xs text-muted-foreground">(leave blank to keep existing)</span>}
                  </Label>
                  <div className="relative">
                    <Input
                      id="smtp_password"
                      type={showPassword ? "text" : "password"}
                      placeholder={hasExistingSettings ? "••••••••" : "Enter password"}
                      value={settings.smtp_password}
                      onChange={(e) => setSettings({...settings, smtp_password: e.target.value})}
                    />
                    <button
                      type="button"
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {/* From Email */}
                <div className="space-y-2">
                  <Label htmlFor="from_email">From Email *</Label>
                  <Input
                    id="from_email"
                    type="email"
                    placeholder="invoices@company.com"
                    value={settings.from_email}
                    onChange={(e) => setSettings({...settings, from_email: e.target.value})}
                  />
                </div>

                {/* From Name */}
                <div className="space-y-2">
                  <Label htmlFor="from_name">From Name</Label>
                  <Input
                    id="from_name"
                    placeholder="Your Company Name"
                    value={settings.from_name}
                    onChange={(e) => setSettings({...settings, from_name: e.target.value})}
                  />
                </div>
              </div>

              {/* Security Options */}
              <div className="flex items-center gap-6 pt-4 border-t">
                <div className="flex items-center gap-2">
                  <Switch
                    id="use_tls"
                    checked={settings.use_tls}
                    onCheckedChange={(checked) => setSettings({...settings, use_tls: checked, use_ssl: checked ? false : settings.use_ssl})}
                  />
                  <Label htmlFor="use_tls" className="flex items-center gap-1">
                    <Shield className="h-4 w-4" />
                    Use TLS
                  </Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    id="use_ssl"
                    checked={settings.use_ssl}
                    onCheckedChange={(checked) => setSettings({...settings, use_ssl: checked, use_tls: checked ? false : settings.use_tls})}
                  />
                  <Label htmlFor="use_ssl" className="flex items-center gap-1">
                    <Shield className="h-4 w-4" />
                    Use SSL
                  </Label>
                </div>
              </div>

              {/* Save Button */}
              <div className="flex justify-end pt-4">
                <Button onClick={handleSave} disabled={saving}>
                  {saving ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="h-4 w-4 mr-2" />
                  )}
                  Save Settings
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Test Connection Card */}
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TestTube className="h-5 w-5" />
                Test Connection
              </CardTitle>
              <CardDescription>
                Send a test email to verify your settings
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-4">
                <div className="flex-1">
                  <Input
                    type="email"
                    placeholder="Enter email to receive test"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                  />
                </div>
                <Button onClick={handleTest} disabled={testing} variant="outline">
                  {testing ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  Send Test
                </Button>
              </div>

              {/* Test Result */}
              {testResult && (
                <div className={`p-4 rounded-lg flex items-center gap-3 ${
                  testResult.success 
                    ? 'bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800'
                    : 'bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800'
                }`}>
                  {testResult.success ? (
                    <CheckCircle className="h-5 w-5 text-emerald-600" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-600" />
                  )}
                  <div>
                    <p className={`font-medium ${testResult.success ? 'text-emerald-900 dark:text-emerald-100' : 'text-red-900 dark:text-red-100'}`}>
                      {testResult.success ? 'Connection Successful!' : 'Connection Failed'}
                    </p>
                    <p className={`text-sm ${testResult.success ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-700 dark:text-red-300'}`}>
                      {testResult.message}
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
  );
};

export default EmailSettings;
